/**
 * V4P Prescription Upload Module
 * 
 * Comprehensive image upload and editing tool following Nielsen's usability heuristics
 * 
 * Features:
 * - Two-tool flow: Enhance Image / Crop & Straighten
 * - File browser upload (PDF, JPEG, PNG)
 * - Phone camera via QR code sync
 * - PDF to PNG conversion (multi-page support)
 * - Image lightbox viewer with tool selection
 * - Enhance Tool: Brightness, Contrast, Saturation, Sharpness, Grayscale
 * - Crop Tool: 4-corner perspective crop with viewfinders + crosshairs
 * - Transform: Rotate 90° CW/CCW, Flip H/V
 * - Auto-enhance preset
 * - Bake enhancements to image
 * 
 * @version 3.0.0
 * @author V4P Platform Team
 */

(function(global) {
  'use strict';

  // ============================================
  // DEFAULT CONFIGURATION
  // ============================================
  const DEFAULT_CONFIG = {
    workerUrl: 'https://rx-upload-sync.automation-b3f.workers.dev',
    fileTypes: ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'],
    maxFileSizeMB: 10,
    phoneUploadTimeoutMs: 120000,
    pollingIntervalMs: 1500,
    autoEnhanceDefault: true,
    enhanceDefaults: {
        brightness: 110,
        contrast: 130,
        saturation: 80
    },
    onUploadComplete: null,
    onUploadRemoved: null,
    onLightboxOpen: null,
    onLightboxClose: null,
    onError: null,

    // ✅ ADD THIS
    onFilenameChange: null,

    getCustomerFirstName: () => '',
    getCustomerLastName: () => ''
    };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  function isMobileDevice() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(ua)) return true;
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return true;
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isNarrow = window.innerWidth <= 1024;
    return isTouch && isNarrow;
  }

  function generateUniqueId() {
    return 'rx_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
  }

  // ============================================
  // MAIN CLASS
  // ============================================
  
  class V4PPrescriptionUpload {
    constructor(options = {}) {
      this.config = { ...DEFAULT_CONFIG, ...options };
      this.container = this.config.container;
      
      if (!this.container) {
        throw new Error('V4PPrescriptionUpload: container element is required');
      }

      // State
      this.prescriptionData = null;
      this.phoneSessionId = null;
      this.phonePollingInterval = null;
      this.phoneUploadTimeout = null;
      this.currentLightboxPage = 0;
      
      // Current tool: 'select' | 'enhance' | 'crop'
      this.currentTool = 'select';
      
      // Crop state
      this.cropPoints = null;
      this.activeCorner = null;

      // DOM references
      this.els = {};

      // Initialize
      this._render();
      this._cacheElements();
      this._bindEvents();
    }

    // ============================================
    // RENDER HTML
    // ============================================
    
    _render() {
      const id = generateUniqueId();
      this.instanceId = id;
      const defaults = this.config.enhanceDefaults;

      this.container.innerHTML = `
        <div class="v4p-upload" data-instance="${id}">
          
          <!-- Upload Dropzone -->
          <div class="v4p-upload__dropzone" data-ref="dropzone">
            <input type="file" data-ref="fileInput" accept="${this.config.fileTypes.join(',')}" hidden>
            
            <!-- Initial State -->
            <div class="v4p-upload__initial" data-ref="initialState">
              <div class="v4p-upload__options" data-ref="optionsPanel">
                <button type="button" class="v4p-upload__option-btn" data-ref="browseBtn">
                  <span class="v4p-upload__option-icon">📁</span>
                  <span class="v4p-upload__option-title">Browse Files</span>
                  <span class="v4p-upload__option-hint">Select from computer</span>
                </button>
                
                <div class="v4p-upload__divider">OR</div>
                
                <button type="button" class="v4p-upload__option-btn" data-ref="phoneBtn">
                  <span class="v4p-upload__option-icon">📱</span>
                  <span class="v4p-upload__option-title">Use Phone Camera</span>
                  <span class="v4p-upload__option-hint">Scan QR & take photo</span>
                </button>
              </div>
              
              <!-- QR Section -->
              <div class="v4p-upload__qr-section" data-ref="qrSection" hidden>
                <button type="button" class="v4p-upload__qr-back" data-ref="qrBackBtn">← Back</button>
                <p class="v4p-upload__qr-title">📱 Scan with your phone</p>
                <div class="v4p-upload__qr-container" data-ref="qrContainer">
                  <div class="v4p-upload__qr-loading">Generating...</div>
                </div>
                <p class="v4p-upload__qr-hint">Scan → Take photo → Auto-uploads here</p>
                <div class="v4p-upload__qr-status" data-ref="qrStatus">
                  <span class="v4p-upload__status-dot"></span>
                  <span class="v4p-upload__status-text" data-ref="qrStatusText">Waiting for phone...</span>
                </div>
              </div>
            </div>
            
            <!-- Success State -->
            <div class="v4p-upload__success" data-ref="successState" hidden>
              <div class="v4p-upload__preview-img-wrap" data-ref="previewWrap" title="Click to edit">
                <img data-ref="previewImg" alt="Uploaded prescription" class="v4p-upload__preview-img">
                <div class="v4p-upload__preview-overlay">
                  <span>🔍 Click to edit</span>
                </div>
              </div>
              <div class="v4p-upload__success-info">
                <span class="v4p-upload__success-icon">✅</span>
                <span class="v4p-upload__success-name" data-ref="filename"></span>
                <button type="button" class="v4p-upload__remove-btn" data-ref="removeBtn">✕ Remove</button>
              </div>
            </div>
          </div>
          
          <!-- Lightbox Modal -->
          <div class="v4p-upload__lightbox" data-ref="lightbox" hidden>
            <div class="v4p-upload__lightbox-backdrop" data-ref="lightboxBackdrop"></div>
            
            <div class="v4p-upload__lightbox-content">
              <div class="v4p-upload__lightbox-img-wrap" data-ref="lightboxImgWrap">
                <button type="button" class="v4p-upload__lightbox-close" data-ref="lightboxClose">✕</button>

                <img data-ref="lightboxImg" alt="Prescription" class="v4p-upload__lightbox-img">

                <!-- Navigation arrows -->
                <button type="button" class="v4p-upload__lightbox-arrow v4p-upload__lightbox-arrow--prev" data-ref="lightboxPrev" hidden>‹</button>
                <button type="button" class="v4p-upload__lightbox-arrow v4p-upload__lightbox-arrow--next" data-ref="lightboxNext" hidden>›</button>

                <!-- 4 Corner Viewfinders with Crosshairs -->
                <div class="v4p-upload__viewfinder v4p-upload__viewfinder--tl" data-corner="0">
                  <img class="v4p-upload__viewfinder-img" alt="">
                  <div class="v4p-upload__viewfinder-crosshair"></div>
                  <span class="v4p-upload__viewfinder-label">TL</span>
                </div>
                <div class="v4p-upload__viewfinder v4p-upload__viewfinder--tr" data-corner="1">
                  <img class="v4p-upload__viewfinder-img" alt="">
                  <div class="v4p-upload__viewfinder-crosshair"></div>
                  <span class="v4p-upload__viewfinder-label">TR</span>
                </div>
                <div class="v4p-upload__viewfinder v4p-upload__viewfinder--br" data-corner="2">
                  <img class="v4p-upload__viewfinder-img" alt="">
                  <div class="v4p-upload__viewfinder-crosshair"></div>
                  <span class="v4p-upload__viewfinder-label">BR</span>
                </div>
                <div class="v4p-upload__viewfinder v4p-upload__viewfinder--bl" data-corner="3">
                  <img class="v4p-upload__viewfinder-img" alt="">
                  <div class="v4p-upload__viewfinder-crosshair"></div>
                  <span class="v4p-upload__viewfinder-label">BL</span>
                </div>

                <!-- Crop Overlay -->
                <div class="v4p-upload__crop-overlay" data-ref="cropOverlay" hidden>
                  <div class="v4p-upload__crop-handle" data-corner="0"><span></span></div>
                  <div class="v4p-upload__crop-handle" data-corner="1"><span></span></div>
                  <div class="v4p-upload__crop-handle" data-corner="2"><span></span></div>
                  <div class="v4p-upload__crop-handle" data-corner="3"><span></span></div>
                </div>
              </div>

              <!-- Footer Toolbar -->
              <div class="v4p-upload__lightbox-footer" data-ref="lightboxFooter">
                
                <!-- TOOL TABS - Quick access navigation -->
                <div class="v4p-upload__tool-tabs" data-ref="toolTabs">
                  <button type="button" class="v4p-upload__tool-tab is-active" data-ref="tabSelect" data-tool="select">
                    <span class="v4p-upload__tool-tab-icon">🏠</span>
                    <span>Tools</span>
                  </button>
                  <button type="button" class="v4p-upload__tool-tab" data-ref="tabEnhance" data-tool="enhance">
                    <span class="v4p-upload__tool-tab-icon">🎨</span>
                    <span>Enhance</span>
                  </button>
                  <button type="button" class="v4p-upload__tool-tab" data-ref="tabCrop" data-tool="crop">
                    <span class="v4p-upload__tool-tab-icon">✂️</span>
                    <span>Crop</span>
                  </button>
                </div>
                
                <!-- TOOL SELECT PANEL (Initial state) -->
                <div class="v4p-upload__tool-select" data-ref="toolSelectPanel">
                  <p class="v4p-upload__tool-title">What would you like to do?</p>
                  <div class="v4p-upload__tool-buttons">
                    <button type="button" class="v4p-upload__tool-btn" data-ref="openEnhanceBtn">
                      <span class="v4p-upload__tool-btn-icon">🎨</span>
                      <span class="v4p-upload__tool-btn-title">Improve Readability</span>
                      <span class="v4p-upload__tool-btn-desc">Adjust brightness, contrast & clarity</span>
                    </button>
                    <button type="button" class="v4p-upload__tool-btn" data-ref="openCropBtn">
                      <span class="v4p-upload__tool-btn-icon">✂️</span>
                      <span class="v4p-upload__tool-btn-title">Crop & Straighten</span>
                      <span class="v4p-upload__tool-btn-desc">Fix skewed photos & crop to prescription</span>
                    </button>
                  </div>
                </div>
                
                <!-- ENHANCE PANEL -->
                <div class="v4p-upload__enhance-panel" data-ref="enhancePanel" hidden>
                  <div class="v4p-upload__panel-body">
                    <div class="v4p-upload__instructions">
                      <p><strong>💡 Improve readability:</strong> Start with <strong>Document</strong> or <strong>Auto-Enhance</strong>, then fine-tune. Changes update the preview instantly — click <strong>Apply Changes</strong> to save them.</p>
                        <p style="margin-top:6px; opacity:.9;">Best results: increase <em>Contrast</em>, slightly increase <em>Brightness</em>, and reduce <em>Saturation</em> to remove colour tint/shadows.</p>
                    </div>
                    
                    <!-- Presets -->
                    <div class="v4p-upload__presets">
                      <span class="v4p-upload__presets-label">Quick Presets</span>
                      <div class="v4p-upload__presets-grid">
                        <button type="button" class="v4p-upload__preset-btn" data-ref="presetOriginal" data-preset="original">
                          <span class="v4p-upload__preset-icon">📷</span>
                          <span>Original</span>
                        </button>
                        <button type="button" class="v4p-upload__preset-btn" data-ref="presetDocument" data-preset="document">
                          <span class="v4p-upload__preset-icon">📄</span>
                          <span>Document</span>
                        </button>
                        <button type="button" class="v4p-upload__preset-btn" data-ref="presetPhoto" data-preset="photo">
                          <span class="v4p-upload__preset-icon">🌅</span>
                          <span>Photo</span>
                        </button>
                        <button type="button" class="v4p-upload__preset-btn" data-ref="presetWhiteboard" data-preset="whiteboard">
                          <span class="v4p-upload__preset-icon">📋</span>
                          <span>Whiteboard</span>
                        </button>
                      </div>
                    </div>
                    
                    <div class="v4p-upload__quick-toggles">
                      <label class="v4p-upload__checkbox">
                        <input type="checkbox" data-ref="autoEnhance" ${this.config.autoEnhanceDefault ? 'checked' : ''}>
                        <span>✨ Auto-Enhance</span>
                      </label>
                      <label class="v4p-upload__checkbox">
                        <input type="checkbox" data-ref="grayscale">
                        <span>🖤 Black & White</span>
                      </label>
                    </div>
                    
                    <div class="v4p-upload__sliders">
                      <!-- Brightness -->
                      <div class="v4p-upload__slider-item">
                        <div class="v4p-upload__slider-header">
                          <label class="v4p-upload__slider-label">
                            <span class="v4p-upload__slider-icon">☀️</span> Brightness
                          </label>
                          <span class="v4p-upload__slider-value" data-ref="brightnessVal">0</span>
                        </div>
                        <p class="v4p-upload__slider-desc">Use if the photo is too dark. Small increases are usually enough.</p>
                        <input type="range" data-ref="brightness" min="-100" max="100" value="0" class="v4p-upload__slider">
                      </div>
                      
                      <!-- Contrast -->
                      <div class="v4p-upload__slider-item">
                        <div class="v4p-upload__slider-header">
                          <label class="v4p-upload__slider-label">
                            <span class="v4p-upload__slider-icon">🎯</span> Contrast
                          </label>
                          <span class="v4p-upload__slider-value" data-ref="contrastVal">0</span>
                        </div>
                        <p class="v4p-upload__slider-desc">Makes text stand out. Increase until handwriting is crisp (avoid “glowing” edges).</p>
                        <input type="range" data-ref="contrast" min="-100" max="100" value="0" class="v4p-upload__slider">
                      </div>
                      
                      <!-- Saturation -->
                      <div class="v4p-upload__slider-item">
                        <div class="v4p-upload__slider-header">
                          <label class="v4p-upload__slider-label">
                            <span class="v4p-upload__slider-icon">🎨</span> Saturation
                          </label>
                          <span class="v4p-upload__slider-value" data-ref="saturateVal">0</span>
                        </div>
                        <p class="v4p-upload__slider-desc">Reduce to remove coloured paper tint, highlighter bleed, and shadow colour.</p>
                        <input type="range" data-ref="saturate" min="-100" max="100" value="0" class="v4p-upload__slider">
                      </div>
                      
                      <!-- Sharpness -->
                      <div class="v4p-upload__slider-item">
                        <div class="v4p-upload__slider-header">
                          <label class="v4p-upload__slider-label">
                            <span class="v4p-upload__slider-icon">🔬</span> Sharpness
                          </label>
                          <span class="v4p-upload__slider-value" data-ref="sharpnessVal">0%</span>
                        </div>
                        <p class="v4p-upload__slider-desc">Adds clarity to edges. Use lightly — too much can add noise.</p>
                        <input type="range" data-ref="sharpness" min="0" max="100" value="0" class="v4p-upload__slider">
                      </div>
                    </div>
                    
                    <div class="v4p-upload__enhance-actions">
                      <button type="button" class="v4p-upload__btn v4p-upload__btn--ghost" data-ref="enhanceReset">↺ Reset All</button>
                      <button type="button" class="v4p-upload__btn v4p-upload__btn--success" data-ref="enhanceApply">✓ Save Enhancements</button>
                    </div>
                  </div>
                </div>
                
                <!-- CROP PANEL - Enhanced -->
                <div class="v4p-upload__crop-panel" data-ref="cropPanel" hidden>
                  <div class="v4p-upload__panel-body">
                    <div class="v4p-upload__instructions v4p-upload__instructions--warning">
                      <p><strong>📌 Straighten & crop:</strong> Drag each <strong>cyan corner handle</strong> so the <strong>red crosshair</strong> sits exactly on the paper corner. Use the zoom boxes to line it up precisely. When all 4 corners match, click <strong>Apply Crop</strong> to straighten and trim.</p>
                        <p style="margin-top:6px; opacity:.9;">Tip: If the photo is sideways, use <strong>Rotate</strong> first. Make sure the <strong>date, prescriber details, and directions</strong> remain visible.</p>
                    </div>
                    
                    <button type="button"
                            class="v4p-upload__auto-detect"
                            data-ref="autoDetectBtn"
                            hidden>
                    <span>🔍</span> Auto-Detect Edges
                    </button>
                    
                    <!-- Visual guides -->
                    <div class="v4p-upload__crop-guides">
                      <div class="v4p-upload__crop-guide-item">
                        <div class="v4p-upload__crop-guide-icon">⬤</div>
                        <div class="v4p-upload__crop-guide-text">
                          <strong>Corner handles</strong>
                          Drag to each paper corner
                        </div>
                      </div>
                      <div class="v4p-upload__crop-guide-item">
                        <div class="v4p-upload__crop-guide-icon" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">🎯</div>
                        <div class="v4p-upload__crop-guide-text">
                          <strong>Target marker</strong>
                          Crosshair shows the exact crop point
                        </div>
                      </div>
                      <div class="v4p-upload__crop-guide-item">
                        <div class="v4p-upload__crop-guide-icon">🔲</div>
                        <div class="v4p-upload__crop-guide-text">
                          <strong>Zoom assist</strong>
                          Use the zoom boxes for precise alignment
                        </div>
                      </div>
                      <div class="v4p-upload__crop-guide-item">
                        <div class="v4p-upload__crop-guide-icon">✨</div>
                        <div class="v4p-upload__crop-guide-text">
                          <strong>Straighten photo</strong>
                            Fixes skew and crops to the document
                        </div>
                      </div>
                    </div>
                    
                    <div class="v4p-upload__transform-section">
                      <span class="v4p-upload__transform-label">Rotate & Flip</span>
                      <div class="v4p-upload__transform-tools">
                        <button type="button" class="v4p-upload__transform-btn" data-ref="rotateCCW" title="Rotate left 90°">
                          <span class="v4p-upload__transform-btn-icon">↶</span>
                          <span>Left 90°</span>
                        </button>
                        <button type="button" class="v4p-upload__transform-btn" data-ref="rotateCW" title="Rotate right 90°">
                          <span class="v4p-upload__transform-btn-icon">↷</span>
                          <span>Right 90°</span>
                        </button>
                        <button type="button" class="v4p-upload__transform-btn" data-ref="flipH" title="Flip horizontal">
                          <span class="v4p-upload__transform-btn-icon">⇆</span>
                          <span>Flip H</span>
                        </button>
                        <button type="button" class="v4p-upload__transform-btn" data-ref="flipV" title="Flip vertical">
                          <span class="v4p-upload__transform-btn-icon">⇅</span>
                          <span>Flip V</span>
                        </button>
                      </div>
                    </div>
                    
                    <div class="v4p-upload__crop-actions">
                      <button type="button" class="v4p-upload__btn v4p-upload__btn--ghost" data-ref="cropCancel">✕ Cancel</button>
                      <button type="button" class="v4p-upload__btn v4p-upload__btn--success" data-ref="cropApply">✓ Straighten & Crop</button>
                    </div>
                  </div>
                </div>
                
                <!-- Footer Meta -->
                <div class="v4p-upload__footer-meta">
                  <div class="v4p-upload__page-nav" data-ref="lightboxNav" hidden>
                    <button type="button" data-ref="lightboxPrevPage">‹</button>
                    <span data-ref="lightboxPageNum">Page 1 of 1</span>
                    <button type="button" data-ref="lightboxNextPage">›</button>
                  </div>
                  <span class="v4p-upload__footer-filename" data-ref="lightboxFilename"></span>
                  <button type="button" class="v4p-upload__footer-done" data-ref="lightboxDone">✓ Close Editor</button>
                </div>
                
              </div>
            </div>
          </div>
          
        </div>
      `;
    }

    // ============================================
    // CACHE ELEMENT REFERENCES
    // ============================================
    
    _cacheElements() {
      const refs = this.container.querySelectorAll('[data-ref]');
      refs.forEach(el => {
        this.els[el.dataset.ref] = el;
      });

      this.els.viewfinders = Array.from(this.container.querySelectorAll('.v4p-upload__viewfinder'));
      this.els.cropHandles = Array.from(this.container.querySelectorAll('.v4p-upload__crop-handle'));
    }

    // ============================================
    // EVENT BINDING
    // ============================================
    
    _bindEvents() {
      const el = this.els;

      // Upload buttons
      el.browseBtn.addEventListener('click', () => this._handleBrowseClick());
      el.phoneBtn.addEventListener('click', () => this._handlePhoneClick());
      el.qrBackBtn.addEventListener('click', () => this._hideQrSection());
      el.fileInput.addEventListener('change', (e) => this._handleFileSelect(e));
      
      // Drag and drop
      el.dropzone.addEventListener('dragover', (e) => this._handleDragOver(e));
      el.dropzone.addEventListener('dragleave', () => this._handleDragLeave());
      el.dropzone.addEventListener('drop', (e) => this._handleDrop(e));
      
      // Remove button
      el.removeBtn.addEventListener('click', () => this.reset());
      
      // Preview click - open lightbox
      el.previewWrap.addEventListener('click', () => this._openLightbox());
      
      // Lightbox close
      el.lightboxClose.addEventListener('click', () => this._closeLightbox());
      el.lightboxBackdrop.addEventListener('click', () => this._closeLightbox());
      el.lightboxDone.addEventListener('click', () => this._handleLightboxDone());
      
      // Page navigation
      el.lightboxPrev.addEventListener('click', () => this._lightboxPrevPage());
      el.lightboxNext.addEventListener('click', () => this._lightboxNextPage());
      
      // Tool tabs
      el.tabSelect.addEventListener('click', () => this._setTool('select'));
      el.tabEnhance.addEventListener('click', () => this._setTool('enhance'));
      el.tabCrop.addEventListener('click', () => this._setTool('crop'));
      
      // Tool selection buttons (from select panel)
      el.openEnhanceBtn.addEventListener('click', () => this._setTool('enhance'));
      el.openCropBtn.addEventListener('click', () => this._setTool('crop'));
      
      // Enhance panel
      el.enhanceReset.addEventListener('click', () => this._resetEnhancements());
      el.enhanceApply.addEventListener('click', () => this._handleEnhanceApply());
      el.autoEnhance.addEventListener('change', () => this._handleAutoEnhanceToggle());
      
      // Presets
      el.presetOriginal.addEventListener('click', () => this._applyPreset('original'));
      el.presetDocument.addEventListener('click', () => this._applyPreset('document'));
      el.presetPhoto.addEventListener('click', () => this._applyPreset('photo'));
      el.presetWhiteboard.addEventListener('click', () => this._applyPreset('whiteboard'));
      
      [el.brightness, el.contrast, el.saturate, el.sharpness, el.grayscale].forEach(input => {
        if (input) input.addEventListener('input', () => this._applyImageEnhancements());
      });
      
      // Crop panel
      el.autoDetectBtn.addEventListener('click', () => this._handleAutoDetect());
      el.cropCancel.addEventListener('click', () => this._exitCropMode());
      el.cropApply.addEventListener('click', () => this._applyCropAndStraighten());
      
      // Transform buttons
      el.rotateCW.addEventListener('click', () => this._rotateImage(90));
      el.rotateCCW.addEventListener('click', () => this._rotateImage(-90));
      el.flipH.addEventListener('click', () => this._flipImage('horizontal'));
      el.flipV.addEventListener('click', () => this._flipImage('vertical'));
      
      // Crop handle events
      this._bindCropHandleEvents();
      
      // Keyboard
      document.addEventListener('keydown', (e) => this._handleKeydown(e));
      
      // Window resize
      window.addEventListener('resize', () => {
        if (this.currentTool === 'crop') this._layoutCropOverlay();
        if (!this.els.lightbox.hidden) this._positionBottomViewfinders();
      });
      
      window.addEventListener('beforeunload', () => this._stopPhonePolling());
    }

    // ============================================
    // TOOL SWITCHING
    // ============================================
    
    _setTool(tool) {
      this.currentTool = tool;
      const el = this.els;

      // Hide all panels
      el.toolSelectPanel.hidden = true;
      el.enhancePanel.hidden = true;
      el.cropPanel.hidden = true;

      // Remove crop mode class
      el.lightbox.classList.remove('is-crop-mode');
      el.cropOverlay.hidden = true;
      
      // Update tab active states
      el.tabSelect.classList.toggle('is-active', tool === 'select');
      el.tabEnhance.classList.toggle('is-active', tool === 'enhance');
      el.tabCrop.classList.toggle('is-active', tool === 'crop');

      switch (tool) {
        case 'select':
          el.toolSelectPanel.hidden = false;
          el.lightboxImg.style.filter = 'none';
          break;
          
        case 'enhance':
          el.enhancePanel.hidden = false;
          this._applyImageEnhancements();
          break;
          
        case 'crop':
          el.cropPanel.hidden = false;
          this._initCropMode();
          break;
      }
    }

    _initCropMode() {
      const el = this.els;
      el.lightbox.classList.add('is-crop-mode');
      
      if (!this.cropPoints) this._initDefaultCropBox();
      el.cropOverlay.hidden = false;

      requestAnimationFrame(() => {
        this._layoutCropOverlay();
        this._positionBottomViewfinders();
      });
    }

    _exitCropMode() {
      this.cropPoints = null;
      this.activeCorner = null;
      this._setTool('select');
    }

    // ============================================
    // CROP HANDLE EVENTS
    // ============================================
    
    _bindCropHandleEvents() {
      const handles = this.els.cropHandles;
      
      handles.forEach((handle, idx) => {
        handle.addEventListener('pointerdown', (evt) => {
          if (this.currentTool !== 'crop') return;
          
          evt.preventDefault();
          evt.stopPropagation();
          
          this.activeCorner = idx;
          handles.forEach(h => h.classList.remove('is-active'));
          handle.classList.add('is-active');
          handle.setPointerCapture(evt.pointerId);
          this._updateAllViewfinders();
        });
      });

      window.addEventListener('pointermove', (evt) => {
        if (this.currentTool !== 'crop' || this.activeCorner === null || !this.cropPoints) return;

        const imgRect = this.els.lightboxImg.getBoundingClientRect();
        const naturalW = this.els.lightboxImg.naturalWidth;
        const naturalH = this.els.lightboxImg.naturalHeight;
        const elemW = imgRect.width;
        const elemH = imgRect.height;
        const naturalRatio = naturalW / naturalH;
        const elemRatio = elemW / elemH;
        
        let renderedW, renderedH, imgOffsetX, imgOffsetY;
        if (naturalRatio > elemRatio) {
          renderedW = elemW;
          renderedH = elemW / naturalRatio;
          imgOffsetX = 0;
          imgOffsetY = (elemH - renderedH) / 2;
        } else {
          renderedH = elemH;
          renderedW = elemH * naturalRatio;
          imgOffsetX = (elemW - renderedW) / 2;
          imgOffsetY = 0;
        }
        
        const actualImgLeft = imgRect.left + imgOffsetX;
        const actualImgTop = imgRect.top + imgOffsetY;
        
        const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
        const relX = clamp((evt.clientX - actualImgLeft) / renderedW, 0, 1);
        const relY = clamp((evt.clientY - actualImgTop) / renderedH, 0, 1);

        this.cropPoints[this.activeCorner] = { nx: relX, ny: relY };
        this._layoutCropOverlay();
        this._updateAllViewfinders();
      });

      window.addEventListener('pointerup', () => {
        this.activeCorner = null;
        this.els.cropHandles.forEach(h => h.classList.remove('is-active'));
        this._clearViewfinderHighlights();
      });

      window.addEventListener('pointercancel', () => {
        this.activeCorner = null;
        this.els.cropHandles.forEach(h => h.classList.remove('is-active'));
        this._clearViewfinderHighlights();
      });
    }

    // ============================================
    // FILE HANDLING
    // ============================================
    
    _handleBrowseClick() {
      const el = this.els;
      el.fileInput.setAttribute('accept', this.config.fileTypes.join(','));
      el.fileInput.removeAttribute('capture');
      el.fileInput.click();
    }

    _handlePhoneClick() {
      if (isMobileDevice()) {
        const el = this.els;
        el.fileInput.setAttribute('accept', 'image/*');
        el.fileInput.setAttribute('capture', 'environment');
        el.qrSection.hidden = true;
        el.optionsPanel.hidden = false;
        el.fileInput.click();
      } else {
        this._showQrSection();
      }
    }

    _handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this._processFile(file);
    }

    _handleDragOver(e) {
      e.preventDefault();
      this.els.dropzone.classList.add('drag-over');
    }

    _handleDragLeave() {
      this.els.dropzone.classList.remove('drag-over');
    }

    _handleDrop(e) {
      e.preventDefault();
      this.els.dropzone.classList.remove('drag-over');
      const file = e.dataTransfer.files[0];
      if (file) this._processFile(file);
    }

    _processFile(file) {
      const validTypes = this.config.fileTypes;
      if (!validTypes.includes(file.type)) {
        this._emitError('Please upload a PDF, JPEG, or PNG file.');
        return;
      }
      
      const maxSize = this.config.maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSize) {
        this._emitError(`File is too large. Maximum size is ${this.config.maxFileSizeMB}MB.`);
        return;
      }
      
      const filename = this._generateFilename('png');
      
      if (file.type.startsWith('image/')) {
        this._convertImageToPNG(file, (pngDataUrl) => {
          this._showUploadSuccess(pngDataUrl, filename);
        });
      } else if (file.type === 'application/pdf') {
        this._convertPDFToPNG(file, (firstPage, allPages) => {
          this._showUploadSuccess(firstPage, filename, allPages);
        });
      }
    }

    _generateFilename(extension = 'png', token = null) {
        const firstname = this.config.getCustomerFirstName() || 'Customer';
        const lastname = this.config.getCustomerLastName() || '';
        
        // If token not provided, create one (used on first upload only)
        const dateStr = token || new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

        const safeFirst = String(firstname).trim() || 'Customer';
        const safeLast  = String(lastname).trim();

        const name = `RX_${safeFirst}${safeLast ? '_' + safeLast : ''}_${dateStr}`;
        return name.replace(/[^a-zA-Z0-9_-]/g, '') + '.' + extension;
        }


    // ============================================
    // IMAGE CONVERSION
    // ============================================
    
    _convertImageToPNG(file, callback) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pngDataUrl = canvas.toDataURL('image/png');
        callback(pngDataUrl);
      };
      img.onerror = () => {
        const reader = new FileReader();
        reader.onload = (e) => callback(e.target.result);
        reader.readAsDataURL(file);
      };
      const reader = new FileReader();
      reader.onload = (e) => { img.src = e.target.result; };
      reader.readAsDataURL(file);
    }

    _convertDataUrlToPNG(dataUrl, callback) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        try {
          callback(canvas.toDataURL('image/png'));
        } catch (e) {
          callback(dataUrl);
        }
      };
      img.onerror = () => callback(dataUrl);
      img.src = dataUrl;
    }

    // ============================================
    // PDF CONVERSION
    // ============================================
    
    _convertPDFToPNG(file, callback) {
      if (typeof pdfjsLib === 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        script.onload = () => {
          pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          this._processPDF(file, callback);
        };
        script.onerror = () => {
          this._emitError('Failed to load PDF converter.');
        };
        document.head.appendChild(script);
      } else {
        this._processPDF(file, callback);
      }
    }

    async _processPDF(file, callback) {
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const typedArray = new Uint8Array(e.target.result);
            const pdf = await pdfjsLib.getDocument(typedArray).promise;
            const numPages = pdf.numPages;
            const scale = 2;
            const pageDataUrls = [];
            
            for (let pageNum = 1; pageNum <= numPages; pageNum++) {
              const page = await pdf.getPage(pageNum);
              const viewport = page.getViewport({ scale });
              
              const canvas = document.createElement('canvas');
              canvas.width = viewport.width;
              canvas.height = viewport.height;
              const ctx = canvas.getContext('2d');
              ctx.fillStyle = '#ffffff';
              ctx.fillRect(0, 0, canvas.width, canvas.height);
              
              await page.render({ canvasContext: ctx, viewport }).promise;
              pageDataUrls.push(canvas.toDataURL('image/png'));
            }
            
            callback(pageDataUrls[0], pageDataUrls);
          } catch (err) {
            this._emitError('Failed to convert PDF.');
          }
        };
        reader.readAsArrayBuffer(file);
      } catch (err) {
        this._emitError('Failed to read PDF file.');
      }
    }

    // ============================================
    // QR CODE / PHONE SYNC
    // ============================================
    
    _showQrSection() {
      const el = this.els;
      el.optionsPanel.hidden = true;
      el.qrSection.hidden = false;
      this._initPhoneUpload();
    }

    _hideQrSection() {
      // Stop polling FIRST before doing anything else
      this._stopPhonePolling();
      
      const el = this.els;
      el.qrSection.hidden = true;
      el.optionsPanel.hidden = false;
      el.qrContainer.innerHTML = '<div class="v4p-upload__qr-loading">Generating...</div>';
      el.qrStatus.classList.remove('connected');
      el.qrStatusText.textContent = 'Waiting for phone...';
    }

    _initPhoneUpload() {
      this.phoneSessionId = generateUniqueId();
      const mobileUrl = `${this.config.workerUrl}/mobile?session=${this.phoneSessionId}`;
      
      const qrImg = document.createElement('img');
      qrImg.src = `https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(mobileUrl)}&color=1e3a5f&bgcolor=ffffff&margin=5`;
      qrImg.alt = 'Scan with phone';
      qrImg.width = 130;
      qrImg.height = 130;
      
      qrImg.onload = () => {
        this.els.qrContainer.innerHTML = '';
        this.els.qrContainer.appendChild(qrImg);
      };
      
      this._startPhonePolling();
    }

    _startPhonePolling() {
      // Prevent duplicate polling intervals
      if (this.phonePollingInterval) {
        return;
      }

      // Clear any existing timeout (but NOT the sessionId)
      if (this.phoneUploadTimeout) {
        clearTimeout(this.phoneUploadTimeout);
        this.phoneUploadTimeout = null;
      }

      this.phoneUploadTimeout = setTimeout(() => {
        this._handlePhoneUploadTimeout();
      }, this.config.phoneUploadTimeoutMs);

      this.els.qrStatusText.textContent = 'Waiting for phone... (2 min timeout)';

      this.phonePollingInterval = setInterval(async () => {
        // Stop if session was cleared
        if (!this.phoneSessionId) {
          this._stopPhonePolling();
          return;
        }

        try {
          const response = await fetch(`${this.config.workerUrl}/poll?session=${this.phoneSessionId}`);
          const data = await response.json();
          
          if (data.found && data.imageData) {
            this._stopPhonePolling();
            this._handlePhoneUpload({ image: data.imageData, filename: data.filename });
          }
        } catch (e) {
          console.error('Polling error:', e);
        }
      }, this.config.pollingIntervalMs);
    }

    _stopPhonePolling() {
      if (this.phonePollingInterval) {
        clearInterval(this.phonePollingInterval);
        this.phonePollingInterval = null;
      }
      if (this.phoneUploadTimeout) {
        clearTimeout(this.phoneUploadTimeout);
        this.phoneUploadTimeout = null;
      }
      // Clear the session ID so no stray polls can happen
      this.phoneSessionId = null;
    }

    _handlePhoneUploadTimeout() {
      this._stopPhonePolling();
      this._hideQrSection();
    }

    _handlePhoneUpload(data) {
      // Stop polling immediately - do this FIRST
      this._stopPhonePolling();
      
      const el = this.els;
      el.qrStatus.classList.add('connected');
      el.qrStatusText.textContent = '✓ Processing photo...';
      
      const filename = this._generateFilename('png');
      
      if (data.image && !data.image.startsWith('data:image/png')) {
        this._convertDataUrlToPNG(data.image, (pngDataUrl) => {
          el.qrStatusText.textContent = '✓ Photo received!';
          this._showUploadSuccess(pngDataUrl, filename);
        });
      } else {
        el.qrStatusText.textContent = '✓ Photo received!';
        this._showUploadSuccess(data.image, filename);
      }
    }

    // ============================================
    // UPLOAD SUCCESS / RESET
    // ============================================
    
    _showUploadSuccess(imageData, filename, pages = null) {
      this._stopPhonePolling();

      let fileType = 'image/png';
      if (imageData.startsWith('data:application/pdf')) fileType = 'application/pdf';
      else if (imageData.startsWith('data:image/jpeg')) fileType = 'image/jpeg';

      // Lock a stable filename token on first successful upload
        if (!this.filenameToken) {
        this.filenameToken = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        }

        // Always derive filename from current customer name + locked token
        const finalFilename = this._generateFilename('png', this.filenameToken);
        filename = finalFilename;

      this.prescriptionData = {
        filename: filename,
        type: fileType,
        data: imageData,
        pages: pages
      };

      let displayName = filename;
      if (pages && pages.length > 1) {
        displayName += ` (${pages.length} pages)`;
      }

      const el = this.els;
      el.previewImg.src = imageData;
      el.filename.textContent = displayName;
      el.initialState.hidden = true;
      el.successState.hidden = false;

      if (typeof this.config.onUploadComplete === 'function') {
        this.config.onUploadComplete(this.prescriptionData);
      }

      setTimeout(() => this._openLightbox(), 300);
    }

    _refreshFilenameFromCustomer() {
        if (!this.prescriptionData) return;

        // Ensure token exists (in case something calls this early)
        if (!this.filenameToken) {
            this.filenameToken = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        }

        const ext = (this.prescriptionData.filename || '').split('.').pop() || 'png';
        const newName = this._generateFilename(ext, this.filenameToken);

        this.prescriptionData.filename = newName;

        // Update UI
        let displayName = newName;
        if (this.prescriptionData.pages && this.prescriptionData.pages.length > 1) {
            displayName += ` (${this.prescriptionData.pages.length} pages)`;
        }
        if (this.els.filename) this.els.filename.textContent = displayName;
        if (this.els.lightboxFilename) this.els.lightboxFilename.textContent = newName;

        // Optional: notify host page (so it can update hidden inputs)
        if (typeof this.config.onFilenameChange === 'function') {
            this.config.onFilenameChange(this.prescriptionData);
        }
        }

    reset() {
      this._stopPhonePolling();
      this.prescriptionData = null;
      this.currentTool = 'select';
      this.cropPoints = null;
      
      const el = this.els;
      el.initialState.hidden = false;
      el.successState.hidden = true;
      el.fileInput.value = '';
      el.fileInput.setAttribute('accept', this.config.fileTypes.join(','));
      el.fileInput.removeAttribute('capture');
      el.optionsPanel.hidden = false;
      el.qrSection.hidden = true;
      el.qrStatus.classList.remove('connected');
      el.qrStatusText.textContent = 'Waiting for phone...';

      if (typeof this.config.onUploadRemoved === 'function') {
        this.config.onUploadRemoved();
      }
    }

    // ============================================
    // LIGHTBOX
    // ============================================
    
    _openLightbox() {
      if (!this.prescriptionData || !this.els.lightbox) return;
      
      const el = this.els;
      
      // Reset to tool selection
      this.currentTool = 'select';
      this.cropPoints = null;
      el.lightbox.classList.remove('is-crop-mode');

      const hasPages = Array.isArray(this.prescriptionData.pages) && this.prescriptionData.pages.length > 0;
      const pagesCount = hasPages ? this.prescriptionData.pages.length : 0;
      const isMultiPage = pagesCount > 1;

      el.lightboxImg.onload = null;

      const handleImageLoad = () => {
        requestAnimationFrame(() => {
          this._positionBottomViewfinders();
        });
      };

      if (hasPages) {
        this.currentLightboxPage = 0;
        el.lightboxImg.onload = handleImageLoad;
        el.lightboxImg.src = this.prescriptionData.pages[0];

        el.lightboxNav.hidden = false;
        el.lightboxPrev.hidden = false;
        el.lightboxPrev.disabled = true;
        el.lightboxNext.hidden = false;
        el.lightboxNext.disabled = !isMultiPage;
        el.lightboxPageNum.textContent = `Page 1 of ${pagesCount}`;
      } else {
        el.lightboxImg.onload = handleImageLoad;
        el.lightboxImg.src = this.prescriptionData.data;
        el.lightboxNav.hidden = true;
        el.lightboxPrev.hidden = true;
        el.lightboxNext.hidden = true;
      }

      el.lightboxFilename.textContent = this.prescriptionData.filename || '';
      el.lightbox.hidden = false;

      document.documentElement.classList.add('v4p-scroll-lock');
        document.body.classList.add('v4p-scroll-lock');
      
      // Show tool selection panel
      this._setTool('select');

      if (typeof this.config.onLightboxOpen === 'function') {
        this.config.onLightboxOpen();
      }
    }

    _closeLightbox() {
      const el = this.els;
      document.documentElement.classList.remove('v4p-scroll-lock');
        document.body.classList.remove('v4p-scroll-lock');
      el.lightbox.hidden = true;
      el.lightbox.classList.remove('is-crop-mode');
      el.lightboxImg.style.filter = 'none';
      this.currentTool = 'select';
      this.cropPoints = null;

      if (typeof this.config.onLightboxClose === 'function') {
        this.config.onLightboxClose();
      }
    }

    _handleLightboxDone() {
    // ✅ ensure filename matches customer name before closing
    this._refreshFilenameFromCustomer();
    this._closeLightbox();
    }

    _handleEnhanceApply() {
      this._bakeEnhancementsToImage(() => {
        this._setTool('select');
      });
    }

    // ============================================
    // PAGE NAVIGATION
    // ============================================
    
    _lightboxPrevPage() {
      if (this.currentLightboxPage > 0) {
        this.currentLightboxPage--;
        this._updateLightboxPage();
      }
    }

    _lightboxNextPage() {
      if (this.prescriptionData.pages && this.currentLightboxPage < this.prescriptionData.pages.length - 1) {
        this.currentLightboxPage++;
        this._updateLightboxPage();
      }
    }

    _updateLightboxPage() {
      if (!this.prescriptionData.pages) return;
      const pages = this.prescriptionData.pages;
      const el = this.els;
      el.lightboxImg.src = pages[this.currentLightboxPage];
      el.lightboxPageNum.textContent = `Page ${this.currentLightboxPage + 1} of ${pages.length}`;
      el.lightboxPrev.disabled = this.currentLightboxPage === 0;
      el.lightboxNext.disabled = this.currentLightboxPage === pages.length - 1;
    }

    _handleKeydown(e) {
      if (this.els.lightbox && !this.els.lightbox.hidden) {
        if (e.key === 'Escape') this._closeLightbox();
        else if (e.key === 'ArrowLeft') this._lightboxPrevPage();
        else if (e.key === 'ArrowRight') this._lightboxNextPage();
      }
    }

    // ============================================
    // IMAGE ENHANCEMENTS
    // ============================================
    
    _applyImageEnhancements() {
        const el = this.els;
        if (!el.lightboxImg) return;

        // Slider raw values: -100..100
        const bRaw = Number(el.brightness?.value ?? 0);
        const cRaw = Number(el.contrast?.value ?? 0);
        const sRaw = Number(el.saturate?.value ?? 0);

        // Convert to CSS filter multipliers
        const b = 1 + (bRaw / 100); // brightness(0..2)
        const c = 1 + (cRaw / 100); // contrast(0..2)
        const s = 1 + (sRaw / 100); // saturate(0..2)

        const g = el.grayscale?.checked ? 1 : 0;
        const sh = Number(el.sharpness?.value ?? 0); // 0..100

        // "Sharpness" proxy: tiny contrast bump (since CSS has no sharpen)
        const sharpContrast = 1 + (sh * 0.003); // up to +0.3

        el.lightboxImg.style.filter =
            `brightness(${b}) contrast(${c * sharpContrast}) saturate(${s}) grayscale(${g})`;

        if (el.brightnessVal) el.brightnessVal.textContent = String(bRaw);
        if (el.contrastVal) el.contrastVal.textContent = String(cRaw);
        if (el.saturateVal) el.saturateVal.textContent = String(sRaw);
        if (el.sharpnessVal) el.sharpnessVal.textContent = `${sh}%`;

        }

    _resetEnhancements() {
      const el = this.els;
      if (el.brightness) el.brightness.value = 0;
        if (el.contrast) el.contrast.value = 0;
        if (el.saturate) el.saturate.value = 0;
      if (el.sharpness) el.sharpness.value = 0;
      if (el.grayscale) el.grayscale.checked = false;
      if (el.autoEnhance) el.autoEnhance.checked = false;
      this._applyImageEnhancements();
    }

    _autoEnhanceImage() {
      const el = this.els;
      if (!el.lightboxImg) return;
      
      if (el.brightness) el.brightness.value = (this.config.enhanceDefaults.brightness - 100);
        if (el.contrast) el.contrast.value = (this.config.enhanceDefaults.contrast - 100);
        if (el.saturate) el.saturate.value = (this.config.enhanceDefaults.saturation - 100);
      if (el.sharpness) el.sharpness.value = 20;
      
      this._applyImageEnhancements();
    }

    _handleAutoEnhanceToggle() {
      if (this.els.autoEnhance.checked) {
        this._autoEnhanceImage();
      } else {
        const el = this.els;
        if (el.brightness) el.brightness.value = 0;
        if (el.contrast) el.contrast.value = 0;
        if (el.saturate) el.saturate.value = 0;
        if (el.sharpness) el.sharpness.value = 0;
        this._applyImageEnhancements();
      }
    }
    
    _applyPreset(preset) {
      const el = this.els;
      
      // Clear all preset active states first
      this._clearPresetActive();
      
      const presets = {
        original:   { brightness: 0,  contrast: 0,  saturate: 0,   sharpness: 0,  grayscale: false },
        document:   { brightness: 15, contrast: 40, saturate: -40, sharpness: 30, grayscale: false },
        photo:      { brightness: 5,  contrast: 10, saturate: 10,  sharpness: 15, grayscale: false },
        whiteboard: { brightness: 25, contrast: 50, saturate: -80, sharpness: 40, grayscale: false }
      };
      
      const p = presets[preset];
      if (!p) return;
      
      if (el.brightness) el.brightness.value = p.brightness;
      if (el.contrast) el.contrast.value = p.contrast;
      if (el.saturate) el.saturate.value = p.saturate;
      if (el.sharpness) el.sharpness.value = p.sharpness;
      if (el.grayscale) el.grayscale.checked = p.grayscale;
      if (el.autoEnhance) el.autoEnhance.checked = false;
      
      // Set the active preset button
      const btnRef = 'preset' + preset.charAt(0).toUpperCase() + preset.slice(1);
      if (el[btnRef]) el[btnRef].classList.add('is-active');
      
      this._applyImageEnhancements();
    }
    
    _clearPresetActive() {
      const el = this.els;
      ['presetOriginal', 'presetDocument', 'presetPhoto', 'presetWhiteboard'].forEach(key => {
        if (el[key]) el[key].classList.remove('is-active');
      });
    }
    
    _handleAutoDetect() {
      // Auto-detect attempts to find document edges
      // For now, we'll set crop points to a slightly inset rectangle
      // A full implementation would use edge detection algorithms
      
      const el = this.els;
      const img = el.lightboxImg;
      if (!img) return;
      
      const rect = img.getBoundingClientRect();
      const wrap = el.lightboxImgWrap.getBoundingClientRect();
      
      // Inset by 5% on each side as a basic "detection"
      const insetX = rect.width * 0.05;
      const insetY = rect.height * 0.05;
      
      const imgLeft = rect.left - wrap.left;
      const imgTop = rect.top - wrap.top;
      
      this.cropPoints = [
        { nx: 0.05, ny: 0.05 },
        { nx: 0.95, ny: 0.05 },
        { nx: 0.95, ny: 0.95 },
        { nx: 0.05, ny: 0.95 }
        ];
      
      this._layoutCropOverlay();
      this._updateAllViewfinders();
      
      // Show a brief feedback
      el.autoDetectBtn.textContent = '✓ Edges Detected';
      setTimeout(() => {
        el.autoDetectBtn.innerHTML = '<span>🔍</span> Auto-Detect Edges';
      }, 1500);
    }

    // ============================================
    // BAKE ENHANCEMENTS
    // ============================================
    
    _bakeEnhancementsToImage(callback) {
      const el = this.els;
      if (!el.lightboxImg || !this.prescriptionData) {
        if (callback) callback();
        return;
      }

      const b = 1 + (Number(el.brightness?.value ?? 0) / 100);
        const c = 1 + (Number(el.contrast?.value ?? 0) / 100);
        const s = 1 + (Number(el.saturate?.value ?? 0) / 100);
      const g = el.grayscale?.checked ? 1 : 0;

      if (b === 1 && c === 1 && s === 1 && g === 0) {
        if (callback) callback();
        return;
      }

      let currentSrc;
      let isFromPages = false;
      let pageIndex = 0;

      if (this.prescriptionData.pages && this.prescriptionData.pages.length > 0) {
        isFromPages = true;
        pageIndex = this.currentLightboxPage || 0;
        currentSrc = this.prescriptionData.pages[pageIndex];
      } else {
        currentSrc = this.prescriptionData.data;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');

        ctx.filter = `brightness(${b}) contrast(${c}) saturate(${s}) grayscale(${g})`;
        ctx.drawImage(img, 0, 0);

        const newDataUrl = canvas.toDataURL('image/png');

        if (isFromPages && this.prescriptionData.pages) {
          this.prescriptionData.pages[pageIndex] = newDataUrl;
          if (pageIndex === 0) this.prescriptionData.data = newDataUrl;
        } else {
          this.prescriptionData.data = newDataUrl;
        }

        el.previewImg.src = newDataUrl;

        // Reset sliders
        if (el.brightness) el.brightness.value = 0;
        if (el.contrast) el.contrast.value = 0;
        if (el.saturate) el.saturate.value = 0;
        if (el.grayscale) el.grayscale.checked = false;
        if (el.autoEnhance) el.autoEnhance.checked = false;

        el.lightboxImg.src = newDataUrl;
        el.lightboxImg.style.filter = 'none';

        if (el.brightnessVal) el.brightnessVal.textContent = '0';
        if (el.contrastVal) el.contrastVal.textContent = '0';
        if (el.saturateVal) el.saturateVal.textContent = '0';

        if (callback) callback();
      };

      img.onerror = () => {
        if (callback) callback();
      };

      img.src = currentSrc;
    }

    // ============================================
    // ROTATE & FLIP
    // ============================================
    
    _rotateImage(degrees) {
      if (!this.prescriptionData) return;

      let currentSrc;
      let isFromPages = false;
      let pageIndex = 0;

      if (this.prescriptionData.pages && this.prescriptionData.pages.length > 0) {
        isFromPages = true;
        pageIndex = this.currentLightboxPage || 0;
        currentSrc = this.prescriptionData.pages[pageIndex];
      } else {
        currentSrc = this.prescriptionData.data;
      }

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const isVertical = Math.abs(degrees) === 90 || Math.abs(degrees) === 270;
        
        canvas.width = isVertical ? img.naturalHeight : img.naturalWidth;
        canvas.height = isVertical ? img.naturalWidth : img.naturalHeight;
        
        const ctx = canvas.getContext('2d');
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(degrees * Math.PI / 180);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        
        const newDataUrl = canvas.toDataURL('image/png');
        this._updateImageData(newDataUrl, isFromPages, pageIndex);
      };

      img.onerror = () => console.error('Failed to load image for rotation');
      img.src = currentSrc;
    }

    _flipImage(direction) {
      if (!this.prescriptionData) return;

      let currentSrc;
      let isFromPages = false;
      let pageIndex = 0;

      if (this.prescriptionData.pages && this.prescriptionData.pages.length > 0) {
        isFromPages = true;
        pageIndex = this.currentLightboxPage || 0;
        currentSrc = this.prescriptionData.pages[pageIndex];
      } else {
        currentSrc = this.prescriptionData.data;
      }

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        
        const ctx = canvas.getContext('2d');
        
        if (direction === 'horizontal') {
          ctx.translate(canvas.width, 0);
          ctx.scale(-1, 1);
        } else {
          ctx.translate(0, canvas.height);
          ctx.scale(1, -1);
        }
        
        ctx.drawImage(img, 0, 0);
        
        const newDataUrl = canvas.toDataURL('image/png');
        this._updateImageData(newDataUrl, isFromPages, pageIndex);
      };

      img.onerror = () => console.error('Failed to load image for flip');
      img.src = currentSrc;
    }

    _updateImageData(newDataUrl, isFromPages, pageIndex) {
      const el = this.els;
      
      if (isFromPages && this.prescriptionData.pages) {
        this.prescriptionData.pages[pageIndex] = newDataUrl;
        if (pageIndex === 0) this.prescriptionData.data = newDataUrl;
      } else {
        this.prescriptionData.data = newDataUrl;
      }

      el.previewImg.src = newDataUrl;
      el.lightboxImg.src = newDataUrl;
      
      // Reset crop points after transform
      this.cropPoints = null;
      if (this.currentTool === 'crop') {
        this._initDefaultCropBox();
        setTimeout(() => this._layoutCropOverlay(), 50);
      }
      
      setTimeout(() => this._positionBottomViewfinders(), 50);
    }

    // ============================================
    // 4-CORNER CROP
    // ============================================
    
    _initDefaultCropBox() {
      this.cropPoints = [
        { nx: 0.12, ny: 0.12 },
        { nx: 0.88, ny: 0.12 },
        { nx: 0.88, ny: 0.88 },
        { nx: 0.12, ny: 0.88 },
      ];
    }

    _positionBottomViewfinders() {
      const el = this.els;
      if (!el.lightboxImg || !el.lightboxFooter || !el.lightboxImgWrap) return;

      const imgRect = el.lightboxImg.getBoundingClientRect();
      const wrapRect = el.lightboxImgWrap.getBoundingClientRect();
      
      const naturalW = el.lightboxImg.naturalWidth;
      const naturalH = el.lightboxImg.naturalHeight;
      if (!naturalW || !naturalH) return;
      
      const elemW = imgRect.width;
      const elemH = imgRect.height;
      const naturalRatio = naturalW / naturalH;
      const elemRatio = elemW / elemH;
      
      let renderedH, imgOffsetY;
      if (naturalRatio > elemRatio) {
        renderedH = elemW / naturalRatio;
        imgOffsetY = (elemH - renderedH) / 2;
      } else {
        renderedH = elemH;
        imgOffsetY = 0;
      }
      
      const imgBottomY = imgRect.top + imgOffsetY + renderedH;
      const distanceFromWrapBottom = wrapRect.bottom - imgBottomY;
      const bottomOffset = Math.max(16, distanceFromWrapBottom + 16);
      
      el.viewfinders.forEach(vf => {
        if (vf.classList.contains('v4p-upload__viewfinder--br') || 
            vf.classList.contains('v4p-upload__viewfinder--bl')) {
          vf.style.bottom = bottomOffset + 'px';
        }
      });
    }

    _layoutCropOverlay() {
      const el = this.els;
      if (!el.cropOverlay || !el.lightboxImg || !el.lightboxImgWrap || !this.cropPoints) return;

      const elemRect = el.lightboxImg.getBoundingClientRect();
      if (elemRect.width < 50 || elemRect.height < 50) {
        setTimeout(() => this._layoutCropOverlay(), 100);
        return;
      }

      const naturalW = el.lightboxImg.naturalWidth;
      const naturalH = el.lightboxImg.naturalHeight;
      if (naturalW === 0 || naturalH === 0) {
        setTimeout(() => this._layoutCropOverlay(), 100);
        return;
      }

      const elemW = elemRect.width;
      const elemH = elemRect.height;
      const naturalRatio = naturalW / naturalH;
      const elemRatio = elemW / elemH;

      let renderedW, renderedH, imgOffsetX, imgOffsetY;
      if (naturalRatio > elemRatio) {
        renderedW = elemW;
        renderedH = elemW / naturalRatio;
        imgOffsetX = 0;
        imgOffsetY = (elemH - renderedH) / 2;
      } else {
        renderedH = elemH;
        renderedW = elemH * naturalRatio;
        imgOffsetX = (elemW - renderedW) / 2;
        imgOffsetY = 0;
      }

      const wrapRect = el.lightboxImgWrap.getBoundingClientRect();
      const totalOffsetX = (elemRect.left - wrapRect.left) + imgOffsetX;
      const totalOffsetY = (elemRect.top - wrapRect.top) + imgOffsetY;

      el.cropOverlay.style.left = `${totalOffsetX}px`;
      el.cropOverlay.style.top = `${totalOffsetY}px`;
      el.cropOverlay.style.width = `${renderedW}px`;
      el.cropOverlay.style.height = `${renderedH}px`;

      el.cropHandles.forEach((h, i) => {
        const pt = this.cropPoints[i];
        h.style.left = `${pt.nx * 100}%`;
        h.style.top = `${pt.ny * 100}%`;
      });

      this._positionBottomViewfinders();
      this._updateAllViewfinders();
    }

    // ============================================
    // VIEWFINDER HELPERS
    // ============================================
    
    _updateAllViewfinders() {
      const el = this.els;
      if (!el.viewfinders.length || !el.lightboxImg || !this.cropPoints) return;

      const imgRect = el.lightboxImg.getBoundingClientRect();
      const naturalW = el.lightboxImg.naturalWidth;
      const naturalH = el.lightboxImg.naturalHeight;
      const elemW = imgRect.width;
      const elemH = imgRect.height;

      if (naturalW === 0 || naturalH === 0) return;

      const naturalRatio = naturalW / naturalH;
      const elemRatio = elemW / elemH;

      let renderedW, renderedH;
      if (naturalRatio > elemRatio) {
        renderedW = elemW;
        renderedH = elemW / naturalRatio;
      } else {
        renderedH = elemH;
        renderedW = elemH * naturalRatio;
      }

      const zoomFactor = 2.5;
      const viewfinderSize = window.innerWidth <= 768 ? 80 : 100;

      el.viewfinders.forEach((vf, idx) => {
        const vfImg = vf.querySelector('.v4p-upload__viewfinder-img');
        if (!vfImg || !this.cropPoints[idx]) return;

        vfImg.src = el.lightboxImg.src;

        const pt = this.cropPoints[idx];
        const imgW = renderedW * zoomFactor;
        const imgH = renderedH * zoomFactor;

        const cx = pt.nx * imgW;
        const cy = pt.ny * imgH;

        vfImg.style.width = `${imgW}px`;
        vfImg.style.height = `${imgH}px`;
        vfImg.style.left = `${-cx + viewfinderSize / 2}px`;
        vfImg.style.top = `${-cy + viewfinderSize / 2}px`;

        vf.classList.toggle('is-active', this.activeCorner === idx);
      });
    }

    _clearViewfinderHighlights() {
      this.els.viewfinders.forEach(vf => vf.classList.remove('is-active'));
    }

    // ============================================
    // PERSPECTIVE WARP / CROP
    // ============================================
    
    _applyCropAndStraighten() {
      const el = this.els;
      if (!el.lightboxImg || !this.cropPoints) return;

      const savedCropPoints = this.cropPoints.map(pt => ({ nx: pt.nx, ny: pt.ny }));

      let currentSrc;
      let isFromPages = false;
      let pageIndex = 0;

      if (this.prescriptionData?.pages?.length > 0) {
        isFromPages = true;
        pageIndex = this.currentLightboxPage || 0;
        currentSrc = this.prescriptionData.pages[pageIndex];
      } else if (this.prescriptionData?.data) {
        currentSrc = this.prescriptionData.data;
      } else {
        return;
      }

      const img = new Image();
      img.onload = () => {
        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;

        const srcPts = savedCropPoints.map(pt => ({
          x: pt.nx * srcW,
          y: pt.ny * srcH
        }));

        const dist = (a, b) => Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

        const topLen = dist(srcPts[0], srcPts[1]);
        const bottomLen = dist(srcPts[3], srcPts[2]);
        const leftLen = dist(srcPts[0], srcPts[3]);
        const rightLen = dist(srcPts[1], srcPts[2]);

        const destW = Math.max(50, Math.round((topLen + bottomLen) / 2));
        const destH = Math.max(50, Math.round((leftLen + rightLen) / 2));

        const srcCanvas = document.createElement('canvas');
        srcCanvas.width = srcW;
        srcCanvas.height = srcH;
        const sctx = srcCanvas.getContext('2d');
        sctx.drawImage(img, 0, 0);

        const srcData = sctx.getImageData(0, 0, srcW, srcH);
        const srcPixels = srcData.data;

        const destCanvas = document.createElement('canvas');
        destCanvas.width = destW;
        destCanvas.height = destH;
        const dctx = destCanvas.getContext('2d');
        const destData = dctx.createImageData(destW, destH);
        const destPixels = destData.data;

        const [p0, p1, p2, p3] = srcPts;

        for (let y = 0; y < destH; y++) {
          const v = y / (destH - 1 || 1);
          const ax = p0.x + (p3.x - p0.x) * v;
          const ay = p0.y + (p3.y - p0.y) * v;
          const bx = p1.x + (p2.x - p1.x) * v;
          const by = p1.y + (p2.y - p1.y) * v;

          for (let x = 0; x < destW; x++) {
            const u = x / (destW - 1 || 1);
            const sx = ax + (bx - ax) * u;
            const sy = ay + (by - ay) * u;

            const sx0 = Math.max(0, Math.min(srcW - 1, Math.round(sx)));
            const sy0 = Math.max(0, Math.min(srcH - 1, Math.round(sy)));

            const srcIdx = (sy0 * srcW + sx0) * 4;
            const destIdx = (y * destW + x) * 4;

            destPixels[destIdx] = srcPixels[srcIdx];
            destPixels[destIdx + 1] = srcPixels[srcIdx + 1];
            destPixels[destIdx + 2] = srcPixels[srcIdx + 2];
            destPixels[destIdx + 3] = srcPixels[srcIdx + 3];
          }
        }

        dctx.putImageData(destData, 0, 0);
        const newDataUrl = destCanvas.toDataURL('image/png');

        if (isFromPages && this.prescriptionData?.pages) {
          this.prescriptionData.pages[pageIndex] = newDataUrl;
          if (pageIndex === 0) this.prescriptionData.data = newDataUrl;
        } else if (this.prescriptionData) {
          this.prescriptionData.data = newDataUrl;
        }

        if (el.previewImg) el.previewImg.src = newDataUrl;
        if (el.lightboxImg) el.lightboxImg.src = newDataUrl;

        // Return to tool selection
        this.cropPoints = null;
        this._setTool('select');
      };

      img.onerror = () => console.error('Failed to load image for cropping');
      img.src = currentSrc;
    }

    // ============================================
    // ERROR HANDLING
    // ============================================
    
    _emitError(message) {
      console.error('V4PPrescriptionUpload:', message);
      if (typeof this.config.onError === 'function') {
        this.config.onError(message);
      }
    }

    // ============================================
    // PUBLIC API
    // ============================================
    
    getData() {
      if (!this.prescriptionData) return null;
      
      // Add aliases for compatibility with V4PCollectPatientData
      return {
        ...this.prescriptionData,
        imageData: this.prescriptionData.data,      // Alias
        imageBase64: this.prescriptionData.data     // Alias
      };
    }
    refreshFilename() {
    this._refreshFilenameFromCustomer();
    }
    hasUpload() { return this.prescriptionData !== null; }
    openLightbox() { this._openLightbox(); }
    closeLightbox() { this._closeLightbox(); }
    destroy() {
      this._stopPhonePolling();
      this.container.innerHTML = '';
      this.els = {};
      this.prescriptionData = null;
    }
  }

  // Export
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = V4PPrescriptionUpload;
  } else {
    global.V4PPrescriptionUpload = V4PPrescriptionUpload;
  }

})(typeof window !== 'undefined' ? window : this);